export type StringOrNumber = string | string[] | number | number[];

// Begin replacing our string | string[] with SafariObjectId
// During the transition we will require some casts etc on existing objects that have been
// defined with just string ID. The hope is that at some point we can remove _parentId
// and SafariObjectId will be either a simple string (base object) or an array of strings
// representing parents AND the actual object
const QUOTE = '"';
const SPACER = ',';
// NOTE: Technically NOID and BLANK are the same but we're using both here to signify different things
// NOID is specifically our "null" value for ID. On the other hand, BLANK, while being the same as NOID behind
// the scenes, is used to signify a scrub value during string manipulation.
const BLANK = '';
export type SafariObjectId = string | number;

type NoHiddenProps<T> = Omit<T, 'error' | 'actionId' | '__etag' | '__priorId' | '__base' | 'context'>;
/**
 * Adapter specific model that forces all properties to be entered.
 * Also excludes reserved property names such as error, etag, etc. If it so happens that one of the future
 * API models has a legitmate property called "error" in its definition, your object will have to define something different like
 * "apiError" or something like that.
 *
 */
export type SafariRequired<T> = Required<NoHiddenProps<T>>;

/**
 * This function ensures that all API properties are covered in the web type we want to create.
 * It will force optional properties to required and therefore any type implementing this type
 * will need to explicitly declare the properties OR omit them if we don't care about them
 *
 * The function will also automatically assign SafariObjectId to property 'id', if it exisits in the original type.
 * It can be also used to convert other types to SafariObjectId before returning the final type
 *
 * First parameter - API type we want to expand
 * Second parameter - OR-ed list of any API properties we want to convert to SafariObjectId
 * Third parameter - Or-ed list of any API properties we want to Omit
 */
export type SafariApiToWebObject<T, K extends string | number | symbol = '', O extends string | number | symbol = ''> = SafariRequired<
  Omit<
    {
      [P in keyof T as P]: P extends K | 'id' ? (T[P] extends Array<any> ? SafariObjectId[] : SafariObjectId) : T[P];
    },
    O
  >
>;

export class SafariObject {
  static EMPTY = {} as any;
  // NOTE: Technically NOID and BLANK are the same but we're using both here to signify different things
  // NOID is specifically our "null" value for ID. On the other hand, BLANK, while being the same as NOID behind
  // the scenes, is used to signify a scrub value during string manipulation.
  static NOID = '';

  id: SafariObjectId;

  // NOTE: error is one of the properties that we extend all of our API objects with. Techincally there is a potential collision issue
  // with the API objects, if ever any one of them had "error" property but in 5 years this has not happened. If it did, we'll just change
  // the adapter for that object. But we need this to be directly on the object so that we can add HttpResponseErrors to it when they come back
  // from the API.
  // However, since we already have this property added we can also use it for UI errors like validation errors or other client-side errors.
  // Those are of string type.
  // But taking this even further - this is JS after all and maybe they could also be of Error type (JS)
  // And, again since this is JS, they could also be of some type we don't know of yet (maybe plain JSON returned by some proxy, or TUS)
  // So TLDR; - error is any. We should use type guards or event better message error parser service downstream if we want some info from errors
  error?: any;
  // Below are properties needed for internal functioning of our framework.
  // You shouldn't be modifying these except maybe, in some special circumstances, etag. We might change
  // "__etag" property name to "etag" (since __ prefix usually signifies "do not use")
  // Also maybe we can figure some trick to implement friend-like classes where framework needs these props
  // so that they can be exposed as public internall inside teh framework (keep strongtypedness), but
  // be private in the actual object definition so nobody tries to use them
  __priorId?: SafariObjectId = null; // don't touch this - reserved for internal create calls

  __etag?: string = null; // usually you don't need to mess with this but sometimes you might need to override
  // might consisder removing leading "__"
  __base?: unknown = null; // don't touch this - reserved for internal 409 auto merge resolution

  static idToArray(arrayOrStringWithCommas: StringOrNumber): string[] {
    // If null/undef/empty was passed in it will return that without trying anymore
    if (!arrayOrStringWithCommas) {
      return [SafariObject.NOID];
    }
    // If this isn't an array it's either a number or string or a commaseparated string
    if (!Array.isArray(arrayOrStringWithCommas)) {
      if (typeof arrayOrStringWithCommas === 'string') {
        // Go to our CSV parser function - this one also does its own trimming on individual IDs, so no need to do anything special with it here
        return SafariObject._parseCSV(arrayOrStringWithCommas);
      }
      // This is neither an array nor string so it has to be a simple number. Just return it as array of one string
      else return [arrayOrStringWithCommas.toString().trim()];
    }
    // At this point it has to be either an array of strings or array of numbers so just return as array of trimmed strings
    return arrayOrStringWithCommas.map(o => (o ? this._scrubString(o.toString()) : SafariObject.NOID));
  }
  static isEmpty<T>(obj: T) {
    return JSON.stringify(obj) === '{}';
  }
  static nullIfEmpty<T>(obj: T): T | null {
    return JSON.stringify(obj) === '{}' ? null : obj;
  }
  static isNewObjectId(id: SafariObjectId) {
    if (!id) {
      return true;
    }
    return SafariObject._idToString(id).includes('-');
  }
  /**
   * This function converts ID to either a comma separated string (if this was an array of IDs)
   * OR just returns the string that was passed in
   * @param arrayOrStringWithCommas Can be '12345' or '123443,23232' or [1232342] or [1233242,12323] or ['1243243', 122432]
   *                                and so forth. But it has to be either a string or number or an array of strings and numbers
   * @returns
   *
   * If you pass in '123' , returns '123'
   * If you pass in 123 , returns '123'
   * If you pass in [123,456] returns '123,456'
   * If you pass in '123,456' returns '123,456'
   */
  private static _idToString(arrayOrStringWithCommas: string[] | number[] | string | number): string {
    const mapNulls = a => {
      if (a === null) {
        return SafariObject.NOID; //null;
      }
      if (a === undefined) {
        return SafariObject.NOID; //undefined;
      }
      if (typeof a === 'string') {
        return SafariObject.NOID;
      }
      if (typeof a === 'number') {
        return '0';
      }
      throw new Error('Unsupported type in SafariObjectId');
    };
    if (!arrayOrStringWithCommas) {
      return mapNulls(arrayOrStringWithCommas);
    }
    if (typeof arrayOrStringWithCommas === 'string') {
      return this._scrubString(arrayOrStringWithCommas);
    }
    if (typeof arrayOrStringWithCommas === 'number') {
      return this._scrubString(arrayOrStringWithCommas.toString());
    }
    if (Array.isArray(arrayOrStringWithCommas)) {
      return arrayOrStringWithCommas.map(o => SafariObject._idToString(o)).join(SPACER);
    }
    throw new Error('Unsupported type in SafariObjectId');
  }
  static id(...args): string {
    let result = SafariObject.NOID;

    if (args.length == 1) {
      result = SafariObject._idToString(args[0]);
    } else {
      const id1 = args[0];
      const id2 = args[1];

      if (id1 == null && id2 == null) {
        result = SafariObject.NOID + SPACER + SafariObject.NOID;
      } else if (id1 == null) {
        result = SafariObject.NOID + SPACER + SafariObject._idToString(id2);
      } else if (id2 == null) {
        result = SafariObject._idToString(id1) + SPACER + SafariObject.NOID;
      } else {
        // Now that we've done basic sanity check let's concat these two
        const parentId = SafariObject.idToArray(id1);

        const childId = SafariObject.idToArray(id2);

        let index = -1;
        for (let i = childId.length - 1; i >= 0; i--) {
          // Let's see if we find any subIDs from childId in parentId
          // (for example - concatinating '1,2,3,4' with '3,4,5')
          index = parentId.findIndex(x => SafariObject.idEqual(x, childId[i]));
          if (index != -1) {
            let testParentIndex = index;
            // We found an overlap between two arrays. Now we have to do a sanity check.
            for (let z = i; z >= 0; z--) {
              if (!SafariObject.idEqual(parentId[testParentIndex], childId[z])) {
                throw new Error('Unuequal IDs with similar hierarchy. ParentId: ' + SafariObject._idToString(parentId) + ', childId: ' + SafariObject._idToString(childId));
              }
              testParentIndex--;
            }
          }
        }
        const finalParentArray = parentId.slice(0, index == -1 ? parentId.length : index);
        result = SafariObject._idToString([...finalParentArray, ...childId]);
      }
      if (args.length > 2) {
        result = SafariObject.id(result, ...args.slice(2));
      }
    }
    return result.replace(/^,*/, BLANK); // remove any leading commas before returning the final result
  }

  private static _scrubString(a): string {
    // Only trim leading and trailing spaces, other than that leave everything else alone
    return a.trim();
  }
  private static _arrayToNonArrayCompare(array: string, notArray: string) {
    return SafariObject.idObjectOnly(array) == notArray;
  }
  static idEqual(a, b): boolean {
    // Let's first convert these two to string.
    // This will make them follow the exact same format and be very easy to compare
    const obj1 = SafariObject._idToString(a);
    const obj2 = SafariObject._idToString(b);

    // Now let's check for a special case of one not being a nested ID and the other one just a basic number/string
    // This is likely to happen especially during early conversion where we still don't have mappers automapping
    // child/parent relationships, etc. In this case we can just compare the last ID in the array with the number/string
    if (SafariObject.idIsCompound(a) && !SafariObject.idIsCompound(b)) {
      return SafariObject._arrayToNonArrayCompare(a, b);
    } else if (SafariObject.idIsCompound(b) && !SafariObject.idIsCompound(a)) {
      return SafariObject._arrayToNonArrayCompare(b, a);
    }
    return obj1 == obj2;
  }
  /**
   *
   * @param id objectId - can be string, number or array of strings or numbers
   * @returns Always returns the objectID only (last item in the array, or itself if not array)
   *
   * If you pass in '123' , returns '123'
   * If you pass in 123 , returns '123'
   * If you pass in [123,456] returns '456'
   * If you pass in '123,456' returns '456'
   *
   */
  static idObjectOnly(id: StringOrNumber): string {
    if (!SafariObject.idIsCompound(id)) {
      return SafariObject._idToString(id);
    } else {
      const allIds = [...SafariObject.idToArray(id)];
      const lastElementId = allIds.pop();
      return lastElementId;
    }
  }
  /**
   *
   * @param id objectId - can be string, number or array of strings or numbers
   * @returns Always returns object's parents only (everything except last item in the array, or [] if not array)
   *
   * If you pass in '123' , returns []
   * If you pass in 123 , returns []
   * If you pass in [123, 456, 789] returns ['123', '456']
   * If you pass in '123,456, 789'  returns ['123', '456']
   *
   */
  static idParentsOnly(id: StringOrNumber) {
    if (!SafariObject.idIsCompound(id)) {
      return [];
    } else {
      const allIds = [...SafariObject.idToArray(id)];
      allIds.pop();
      return allIds;
    }
  }
  /**
   * This looks like it should be in crud service but we don't want it there. Some UI code might
   * sometimes want to parse an endpoint (like for example to get a public URL for a file or something like that)
   * And we definitely don't want UI to import crud.service.ts. So we'll keep this here and we'll
   * eventually repoint crud.service to use this
   * @param id ID of the object
   * @param endpoint endpoint in our standard form such as /parent/{0}/child{1}/child2 etc
   * @returns Final endpoint with correct substitutions
   */
  static formatEndpoint(id: StringOrNumber, endpoint: string, useObjectId = true): string {
    let objectAndParents: { objectId: string; parentIds: string[] } = null;
    if (!SafariObject.idIsCompound(id)) {
      objectAndParents = { objectId: SafariObject._idToString(id), parentIds: [] };
    } else {
      const parentIds = [...SafariObject.idToArray(id)];
      const lastElementId = parentIds.pop() || null;

      objectAndParents = { objectId: lastElementId, parentIds: parentIds || [] };
    }

    for (let i = 0; i < objectAndParents.parentIds.length; i++) {
      endpoint = endpoint.replace('{' + i.toString() + '}', objectAndParents.parentIds[i]);
    }
    if (useObjectId) {
      return (endpoint + '/' + (objectAndParents.objectId || SafariObject.NOID)).replace(/\/+$/, BLANK);
    } else {
      return (endpoint + '/').replace(/\/+$/, BLANK);
    }
  }
  /**
   * If what's passed in is an array or a comma separated string
   *
   * @param id
   * @returns
   */
  static idIsCompound(id: string | number | string[] | number[]) {
    if (id == null) {
      return false;
    }
    if (typeof id === 'number') {
      return false;
    }
    if (Array.isArray(id)) {
      return true;
    }
    return id.includes(SPACER);
  }

  private static _parseCSV(str: string) {
    // Lifted from SO - seems to work well

    //split the str first
    //then merge the elments between two double quotes

    const elements = str.split(SPACER);
    const newElements = [];
    for (let i = 0; i < elements.length; ++i) {
      if (elements[i].indexOf(QUOTE) >= 0) {
        //the left double quotes is found
        let indexOfRightQuotes = -1;
        let tmp = elements[i];
        //find the right double quotes
        for (let j = i + 1; j < elements.length; ++j) {
          if (elements[j].indexOf(QUOTE) >= 0) {
            indexOfRightQuotes = j;
            break;
          }
        }
        //found the right double quotes
        //merge all the elements between double quotes
        if (-1 != indexOfRightQuotes) {
          for (let j = i + 1; j <= indexOfRightQuotes; ++j) {
            tmp = tmp + SPACER + elements[j];
          }
          newElements.push(tmp);
          i = indexOfRightQuotes;
        } else {
          //right double quotes is not found
          newElements.push(elements[i]);
        }
      } else {
        //no left double quotes is found
        newElements.push(elements[i]);
      }
    }
    //  return newElements;
    return newElements.map(o => this._scrubString(o.trim()));
  }
}

export class SafariBulkObject<T> extends SafariObject {
  objects: T[];
}
